import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="send-test"
//
// The "Send me a test" button reports the send itself rather than a banner
// above the form: disabled while the request is in flight, then a drawn check,
// then back to the idle label. The server answers 204, so nothing else on the
// sheet moves and its scroll position is kept.
//
// The confirmation is "Test sent", not "delivered" — the 204 means the job was
// enqueued. A failure the browser can see (422, dropped connection) swaps in
// the failed label; a job that dies after enqueueing cannot reach this.
//
// Exactly one label carries a display class at a time. Toggling `hidden` alone
// would leave two display utilities on the span, and source order would decide
// which one wins.
export default class extends Controller {
  static targets = ["idle", "sent", "failed", "button"];
  static values = { revertAfter: { type: Number, default: 3000 } };

  declare readonly idleTarget: HTMLElement;
  declare readonly sentTarget: HTMLElement;
  declare readonly failedTarget: HTMLElement;
  declare readonly buttonTarget: HTMLButtonElement;
  declare revertAfterValue: number;
  declare timeout: ReturnType<typeof setTimeout> | undefined;

  disconnect() {
    clearTimeout(this.timeout);
  }

  // Fires once Turbo has the request in flight, so disabling here cannot
  // cancel the submission. Also clears a label still showing from a previous
  // send, which a click during the revert delay would otherwise leave up.
  start() {
    clearTimeout(this.timeout);
    this.showOnly(this.idleTarget);
    this.buttonTarget.disabled = true;
  }

  finish(event: CustomEvent<{ success?: boolean }>) {
    const sent = event.detail?.success === true;

    this.showOnly(sent ? this.sentTarget : this.failedTarget);
    // Success holds the button closed so a second click cannot land while the
    // check draws; a failure re-opens it immediately so the retry is one click.
    this.buttonTarget.disabled = sent;
    this.timeout = setTimeout(() => this.reset(), this.revertAfterValue);
  }

  private reset() {
    this.showOnly(this.idleTarget);
    this.buttonTarget.disabled = false;
  }

  private showOnly(active: HTMLElement) {
    for (const label of [this.idleTarget, this.sentTarget, this.failedTarget]) {
      const visible = label === active;
      label.classList.toggle("hidden", !visible);
      label.classList.toggle("inline-flex", visible);
    }
  }
}
